import { useEffect, useState } from "react";

import type { SearchHit } from "../results/types";
import { commandPaletteApi } from "../services";

const DEBOUNCE_MS = 200;
const RESULT_LIMIT = 50;

export function useGlobalSearch(
  query: string,
  types: string[],
  ids: number[],
  enabled: boolean,
) {
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [totalHits, setTotalHits] = useState<number | undefined>(undefined);
  const [loading, setLoading] = useState(false);

  const active =
    enabled && (query.trim().length > 0 || types.length > 0 || ids.length > 0);

  useEffect(() => {
    if (!active) {
      return;
    }

    const controller = new AbortController();

    const timeout = window.setTimeout(() => {
      const search = async () => {
        setLoading(true);

        try {
          const response = await commandPaletteApi.fetchGlobalSearch(
            query,
            types,
            ids,
            RESULT_LIMIT,
            { signal: controller.signal },
          );

          setHits(response.hits);
          setTotalHits(response.estimatedTotalHits);
        } catch (error) {
          if (controller.signal.aborted) {
            return;
          }

          console.error("Global search failed:", error);
          setHits([]);
          setTotalHits(undefined);
        } finally {
          if (!controller.signal.aborted) {
            setLoading(false);
          }
        }
      };

      search();
    }, DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [query, types, ids, active]);

  return {
    hits: active ? hits : [],
    totalHits: active ? totalHits : undefined,
    loading: active && loading,
  };
}
